import React from 'react';
import { Badge, Crown, Star, Zap } from 'lucide-react';
import { LogoIcon } from '@/assets/logo';
import { cn } from '@/utils';
import { useRestaurantStore } from '@/stores/restaurantStore';
import { useSafeTranslation } from '@/hooks/useSafeTranslation';
import type { SubscriptionPlan } from '@/types';

// Partnership Badge Component
interface PartnershipBadgeProps {
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'minimal' | 'prominent';
  className?: string;
}

export const PartnershipBadge: React.FC<PartnershipBadgeProps> = ({
  size = 'md',
  variant = 'default',
  className,
}) => {
  const { t } = useSafeTranslation();

  const sizeClasses = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-3 py-1.5 text-sm',
    lg: 'px-4 py-2 text-base',
  };

  const variantClasses = {
    default: 'bg-neutral-50 text-neutral-700 border border-neutral-300',
    minimal: 'bg-neutral-100 text-neutral-600 border border-neutral-200',
    prominent: 'bg-warning-600 text-white shadow-lg',
  };

  return (
    <div className={cn(
      'inline-flex items-center rounded-full font-medium',
      sizeClasses[size],
      variantClasses[variant],
      className
    )}>
      <LogoIcon
        size={size === 'sm' ? 12 : size === 'md' ? 15 : 18}
        className="mr-1.5 flex-shrink-0"
      />
      <span>{t('partnership.partner')}</span>
    </div>
  );
};

// Restaurant Header Component
interface RestaurantHeaderProps {
  showLogo?: boolean;
  showBadge?: boolean;
  className?: string;
}

export const RestaurantHeader: React.FC<RestaurantHeaderProps> = ({
  showLogo = true,
  showBadge = true,
  className,
}) => {
  const { t } = useSafeTranslation();
  const { restaurant, getDisplayName } = useRestaurantStore();

  return (
    <div className={cn('flex items-center space-x-3', className)}>
      {showLogo && restaurant?.branding?.logo && (
        <img
          src={restaurant.branding.logo}
          alt={t('partnership.restaurantLogoAlt')}
          className="h-10 w-10 rounded-lg object-cover"
        />
      )}
      <div className="flex-1">
        <h1 className="text-xl font-semibold tracking-tight text-neutral-900">
          {getDisplayName()}
        </h1>
        {showBadge && (
          <PartnershipBadge size="sm" variant="minimal" />
        )}
      </div>
    </div>
  );
};

// Co-branded Title Component
interface CoBrandedTitleProps {
  restaurantName?: string;
  fontSize?: 'sm' | 'base' | 'lg' | 'xl' | '2xl';
  className?: string;
}

export const CoBrandedTitle: React.FC<CoBrandedTitleProps> = ({
  restaurantName,
  fontSize = 'xl',
  className,
}) => {
  const { t } = useSafeTranslation();
  const { getDisplayName } = useRestaurantStore();
  const name = restaurantName || getDisplayName() || 'Nova Cafe';

  const fontSizeClasses = {
    sm: 'text-sm',
    base: 'text-base',
    lg: 'text-lg',
    xl: 'text-xl',
    '2xl': 'text-2xl',
  };

  return (
    <h1 className={cn(
      'font-bold text-neutral-900',
      fontSizeClasses[fontSize],
      className
    )}>
      {name} <span className="text-neutral-500">•</span>{' '}
      <span className="text-neutral-500">
        {t('partnership.platform')}
      </span>
    </h1>
  );
};

// Partnership Message Component
interface PartnershipMessageProps {
  variant?: 'default' | 'footer' | 'receipt';
  className?: string;
}

export const PartnershipMessage: React.FC<PartnershipMessageProps> = ({
  variant = 'default',
  className,
}) => {
  const { t } = useSafeTranslation();

  // All three variants are Nova's own copy, not per-restaurant text. They used
  // to be English literals here (and a `brandingPreferences.receiptMessage`
  // that no endpoint populates), which is how "Part of the Nova Cafe
  // Restaurant Network" kept rendering untranslated in the sidebar after the
  // same string was fixed on the login page.
  const getMessage = () => {
    switch (variant) {
      case 'footer':
        return t('partnership.networkMember');
      case 'receipt':
        return t('partnership.receiptThanks');
      default:
        return t('partnership.poweredBy');
    }
  };

  return (
    <p className={cn(
      'text-sm text-neutral-600 flex items-center',
      className
    )}>
      <LogoIcon size={15} className="mr-2 flex-shrink-0 text-neutral-500" />
      {getMessage()}
    </p>
  );
};

// Subscription Plan Badge
interface PlanBadgeProps {
  plan?: SubscriptionPlan;
  showUpgrade?: boolean;
  className?: string;
}

export const PlanBadge: React.FC<PlanBadgeProps> = ({
  plan,
  showUpgrade = false,
  className,
}) => {
  const { t } = useSafeTranslation();
  const { restaurant } = useRestaurantStore();
  // The plan the backend actually issued for this restaurant. There is no
  // default: without a restaurant record we show no badge rather than
  // guessing a tier.
  const displayPlan = plan || restaurant?.subscription.plan;

  if (!displayPlan) return null;

  const getPlanIcon = (planId: SubscriptionPlan) => {
    switch (planId) {
      case 'basic':
        return <Zap className="h-4 w-4" />;
      case 'premium':
        return <Star className="h-4 w-4" />;
      case 'enterprise':
        return <Crown className="h-4 w-4" />;
      default:
        return <Badge className="h-4 w-4" />;
    }
  };

  const getPlanColor = (planId: SubscriptionPlan) => {
    switch (planId) {
      case 'basic':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'premium':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'enterprise':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-neutral-100 text-neutral-800 border-neutral-200';
    }
  };

  return (
    <div className={cn(
      'inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border',
      getPlanColor(displayPlan),
      className
    )}>
      {getPlanIcon(displayPlan)}
      <span className="ml-1.5">{t(`partnership.plans.${displayPlan}`)}</span>
      {showUpgrade && displayPlan !== 'enterprise' && (
        <span className="ml-2 text-xs text-neutral-500">
          {t('partnership.upgradeAvailable')}
        </span>
      )}
    </div>
  );
};

// Partnership Value Proposition Component
export const PartnershipValueProps: React.FC<{ className?: string }> = ({ className }) => {
  const { t } = useSafeTranslation();
  const benefits = [
    t('partnership.benefits.joinNetwork'),
    t('partnership.benefits.provenTechnology'),
    t('partnership.benefits.modernRevolution'),
    t('partnership.benefits.community'),
  ];

  return (
    <div className={cn('space-y-2', className)}>
      {benefits.map((benefit, index) => (
        <div key={index} className="flex items-center text-sm text-neutral-700">
          <div className="h-2 w-2 rounded-full bg-neutral-400 mr-3" />
          {benefit}
        </div>
      ))}
    </div>
  );
};

/**
 * Subscription status banner.
 *
 * This was a "trial" banner that showed a hardcoded "7 days remaining" for any
 * restaurant whose partnershipStatus was 'trial' — a status the backend never
 * issues, counting down a number that came from a function whose body was
 * `return 7; // Mock 7 days remaining`. The subscription record does carry a
 * real `expiresAt`, so that is what this counts, and it only speaks up when
 * there is something true to say.
 */
const EXPIRY_WARNING_DAYS = 14;

export const SubscriptionStatusBanner: React.FC = () => {
  const { t } = useSafeTranslation();
  const { restaurant } = useRestaurantStore();

  if (!restaurant) return null;

  const { status, expiresAt } = restaurant.subscription;
  const daysRemaining = expiresAt
    ? Math.ceil((new Date(expiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;

  const isExpiringSoon =
    daysRemaining !== null && daysRemaining >= 0 && daysRemaining <= EXPIRY_WARNING_DAYS;
  const isInactive = status !== 'active';

  if (!isInactive && !isExpiringSoon) return null;

  return (
    <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-4 mb-6">
      <div className="flex items-center">
        <div className="h-2 w-2 rounded-full bg-neutral-400 mr-3 flex-shrink-0" />
        <div>
          <p className="text-sm font-medium text-neutral-900">
            {t(`partnership.subscriptionStatus.${status}`)}
          </p>
          {daysRemaining !== null && (
            <p className="text-xs text-neutral-500">
              {daysRemaining >= 0
                ? t('partnership.subscriptionExpiresIn', { count: daysRemaining })
                : t('partnership.subscriptionExpired')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// Platform Footer Message (for login page - doesn't use restaurant store)
export const PlatformFooterMessage: React.FC<{ className?: string }> = ({ className }) => {
  const { t } = useSafeTranslation();
  return (
    // justify-center: this sits inside a text-center footer, and a bare flex
    // row would otherwise align left and break the column.
    <p className={cn(
      'text-sm text-neutral-500 flex items-center justify-center',
      className
    )}>
      <LogoIcon size={15} className="mr-2 flex-shrink-0 text-neutral-400" />
      {t('partnership.networkMember')}
    </p>
  );
};

// Mobile Partnership Footer
export const MobilePartnershipFooter: React.FC = () => {
  const { t } = useSafeTranslation();
  const { restaurant } = useRestaurantStore();

  return (
    <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-neutral-200 p-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          {restaurant?.branding?.logo && (
            <img
              src={restaurant.branding.logo}
              alt={t('partnership.restaurantLogoAlt')}
              className="h-6 w-6 rounded object-cover"
            />
          )}
          <span className="text-sm font-medium text-neutral-900">
            {restaurant?.name}
          </span>
        </div>
        <PartnershipBadge size="sm" variant="minimal" />
      </div>
    </div>
  );
};

export default {
  PartnershipBadge,
  RestaurantHeader,
  CoBrandedTitle,
  PartnershipMessage,
  PlatformFooterMessage,
  PlanBadge,
  PartnershipValueProps,
  SubscriptionStatusBanner,
  MobilePartnershipFooter,
};